import type { ReactNode } from "react";
import { FileText, Loader2, RotateCw, Square } from "lucide-react";

import { ElapsedTimer } from "@/components/ring-central/ongoing-call-indicator";
import { Button } from "@/components/ui/button";
import { useKoryTray } from "@/contexts/kory-tray-context";
import { cn } from "@/lib/utils";

import { RecordingDot } from "./meeting-controls";
import { useMeetingMode, useWaitingSuggestions } from "./meeting-mode";

/**
 * A folded tray's voice controls (the side pane's 56px rail, or a phone's collapsed bar): the one
 * voice button. The gear menu in the open tray switches Personal ↔ Meeting. Personal: `talk` (Talk).
 * Meeting: Listen; while recording, the timer, the waiting-suggestion count and Stop, each opening
 * the tray where the rest of it is.
 * Interrupted: an amber Resume in place of the timer. Starting: a spinner until capture records.
 * Following another tab's recording: the same timer and Stop, titled so.
 */
export function MeetingRailSlot({ bar, talk }: { bar: boolean; talk: ReactNode }) {
  const m = useMeetingMode();
  const openTray = useKoryTray()?.openTray;
  const waiting = useWaitingSuggestions(m.mode === "meeting" ? (m.meeting?.id ?? null) : null, m.live);
  if (!m.capable || m.mode === "personal") return <>{talk}</>;
  const count = waiting.data?.length ?? 0;
  const open = (then?: () => void) => () => {
    openTray?.();
    then?.();
  };
  const paused = m.paused;
  const clockLabel = m.role === "follower" ? "Recording in another tab. Open the meeting" : "Recording. Open the meeting";
  return (
    <>
      {m.live && m.meeting ? (
        <>
          {m.phase === "interrupted" ? (
            <button
              type="button"
              data-testid="meeting-rail-resume"
              aria-label="Not listening. Resume Recording"
              title="Not listening. Resume Recording"
              onClick={open(() => void m.resumeRecording())}
              className={cn(
                "flex items-center justify-center gap-1 rounded-md border border-warning bg-warning/15 font-semibold text-foreground hover:bg-warning/25 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                bar ? "h-11 px-3 text-sm" : "h-11 w-12 flex-col gap-0.5 text-[11px]",
              )}
            >
              <RotateCw className={bar ? "size-4" : "size-3.5"} aria-hidden />
              Resume
            </button>
          ) : m.phase === "starting" ? (
            <span
              role="status"
              data-testid="meeting-rail-starting"
              aria-label="Starting the recording"
              title="Starting the recording"
              className={cn("grid place-items-center text-muted-foreground", bar ? "h-11 px-2" : "h-8 w-12")}
            >
              <Loader2 className="size-4 animate-spin" aria-hidden />
            </span>
          ) : (
          <button
            type="button"
            data-testid="meeting-rail-clock"
            aria-label={clockLabel}
            title={m.role === "follower" ? "Recording in another tab" : undefined}
            onClick={open()}
            className={cn(
              "flex items-center justify-center gap-1 rounded-md font-semibold text-destructive hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
              bar ? "h-11 px-2 text-sm" : "h-8 w-12 flex-col gap-0.5 text-[11px]",
            )}
          >
            <RecordingDot paused={paused} className={bar ? undefined : "size-2"} />
            <ElapsedTimer since={m.meeting.started_at} />
          </button>
          )}
          {count ? (
            <button
              type="button"
              data-testid="meeting-rail-waiting"
              aria-label={`${count} ${count === 1 ? "suggestion" : "suggestions"} waiting`}
              title={`${count} waiting`}
              onClick={open()}
              className={cn(
                "grid place-items-center rounded-full bg-primary font-semibold text-primary-foreground tabular-nums focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                bar ? "h-8 min-w-8 px-2 text-sm" : "size-7 text-xs",
              )}
            >
              {count}
            </button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            aria-label="Stop recording"
            title="Stop recording"
            disabled={m.phase === "stopping" || m.phase === "starting"}
            onClick={open(m.askStop)}
            className={cn(
              "border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive",
              bar ? "h-11 gap-1 px-3 text-sm" : "size-11 p-0",
            )}
          >
            <Square className={bar ? "size-3 fill-current" : "size-4 fill-current"} aria-hidden />
            {bar ? "Stop" : null}
          </Button>
        </>
      ) : m.phase === "after" ? (
        <Button
          type="button"
          variant="outline"
          aria-label="The meeting's notes"
          onClick={open()}
          className={bar ? "h-11 gap-1.5 px-3 text-sm" : "size-11 p-0"}
        >
          <FileText className="size-4" aria-hidden />
          {bar ? "Notes" : null}
        </Button>
      ) : (
        <Button
          type="button"
          variant="outline"
          data-testid="meeting-rail-listen"
          onClick={open(m.openSheet)}
          className={cn(
            "rounded-full border-destructive/50 bg-card font-semibold text-destructive hover:bg-destructive/10 hover:text-destructive",
            bar ? "h-11 gap-1.5 px-4 text-sm" : "h-8 gap-1 px-2 text-xs",
          )}
        >
          {bar ? <RecordingDot paused /> : null}
          Listen
        </Button>
      )}
    </>
  );
}
